/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Sentry from "@sentry/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Toaster } from "react-hot-toast";
import { Provider } from "react-redux";
import { persistStore } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import { AnalyticsProvider } from "./analytics/AnalyticsProvider.tsx";
import LogoTeal from "./assets/LogoTeal.svg";
import { CookieConsentBanner } from "./components/Common/CookieBanner/CookieConsentBanner.tsx";
import LoadingScreen from "./components/Common/LoadingScreen.tsx";
import { SessionLogoutWarning } from "./components/Common/SessionLogoutWarning.tsx";
import "./i18n/config.ts";
import "./index.css";
import Router from "./routes/index.tsx";
import { store } from "./store/index.ts";
import { initWizardConfig } from "./config/wizardConfig.ts";
import { getTenantFromHash } from "./utils/getBrandings.ts";

declare global {
  interface Window {
    partner: Record<string, any>;
  }
}

Sentry.init({
  dsn: import.meta.env.VITE_APP_DEV_SENTRY_DNS,
  integrations: [Sentry.replayIntegration()],
  // Session Replay
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

const persistor = persistStore(store);

async function loadTenantConfig() {
  if (import.meta.env.VITE_APP_DISABLE_TENANT_FETCH === "true") {
    window.partner = {
      title: "Sootchy",
      logo: "",
      favicon: "",
      colors: {
        primary: "#24C0BE",
        secondary: "#99EBE1",
      },
    };
    return;
  }
  const tenantName = getTenantFromHash();
  const res = await fetch(
    `${import.meta.env.VITE_APP_API_BASE_URL}/Employer/tenants`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Ocp-Apim-Subscription-Key": import.meta.env.VITE_APP_API_KEY,
      },
    },
  );

  const partnerJSON = await res.json();

  if (!partnerJSON.data.length) {
    window.partner = {
      title: "Sootchy",
      logo: LogoTeal,
      favicon: "/favicon-32x32.png",
      colors: {
        primary: "#24C0BE",
        secondary: "#99EBE1",
      },
    };
    return;
  }

  const tenantFound = partnerJSON.data.find(
    (item: any) => item.subdomain === tenantName,
  );

  if (!tenantFound) {
    window.partner = {
      title: "Sootchy",
      logo: LogoTeal,
      favicon: "/favicon-32x32.png",
      colors: {
        primary: "#24C0BE",
        secondary: "#99EBE1",
      },
    };
    return;
  }

  const tranformedTenant = {
    title: tenantFound.brandName,
    logo: tenantFound.logo,
    favicon: tenantFound.favicon,
    colors: {
      primary: tenantFound.primaryColor,
      secondary: tenantFound.secondaryColor,
    },
  };
  const partner = tranformedTenant || {};
  window.partner = partner;

  // setBrandColors({ brandColor: partner.colors.primary });
}

const root = createRoot(document.getElementById("root")!);

root.render(<LoadingScreen />);

const renderApp = () => {
  root.render(
    <StrictMode>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <AnalyticsProvider>
            <CookieConsentBanner />
            <Toaster position="top-center" />
            <SessionLogoutWarning />
            <Router />
          </AnalyticsProvider>
        </PersistGate>
      </Provider>
    </StrictMode>,
  );
};

initWizardConfig(); // fire-and-forget — defaults are always ready, 3 s timeout prevents hangs

loadTenantConfig()
  .then(() => {
    renderApp();
  })
  .catch(() => {
    if (!window.partner) {
      window.partner = {
        title: "Sootchy",
        logo: LogoTeal,
        favicon: "/favicon-32x32.png",
        colors: {
          primary: "#24C0BE",
          secondary: "#99EBE1",
        },
      };
    }
    renderApp();
  });
